import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {useNotifications} from "context/NotificationContext";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "context/AuthContext";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {BookmarkIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {EventType} from "types/EventType";
import ConfirmModal from "components/ConfirmModal";
import Icon from "components/Icon";
import EventTypeContextMenu from "components/menus/EventTypeContextMenu";
import NotAuthorised from "pages/NotAuthorised";

// Component ---

function EventTypeList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [deletingEventType, setDeletingEventType] = useState<EventType | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['event-types.all', {page}],
    () => ngage.eventTypes.all(page),
    {keepPreviousData: true, enabled: auth.can('event-types:read')}
  )

  const {mutateAsync: deleteEventType} = useMutation(ngage.eventTypes.destroy)

  // Prepare action handlers
  async function handleDeleteEventType(eventType: EventType) {
    if (eventType.id) {
      try {
        await deleteEventType(eventType.id)

        setDeletingEventType(null)
        notify("success", `${termFor('Event Type')} Deleted`, `${eventType.name} has been permanently removed`)

        return invalidate('event-types.all')
      } catch (error) {
        setDeletingEventType(null)
        notify("error", `${termFor('Event Type')} Not Deleted`, `${eventType.name} could not be removed`)
      }
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('event-types:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('event-types.all', page, ngage.eventTypes.all)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('event-types:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/event-types/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'App Content': '/',
    [termFor('Events')]: '/',
    [termFor('Event Types')]: '/event-types'
  }

  return (
    <Page
      title={termFor('Event Types')}
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>Events</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <BookmarkIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('event types')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((eventType, index) => (
              <TableRow key={eventType.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <EventTypeContextMenu
                        eventType={eventType}
                        onDeleteEventType={() => setDeletingEventType(eventType)}
                        deleteDisabled={eventType.event_count > 0}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <Icon
                        name={eventType.icon}
                        className="h-5 w-5 mr-2 text-gray-500"/> {eventType.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{truncate(eventType.description)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{eventType.event_count}</TableData>
                <TableData>{formattedDateTime(eventType.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <EventTypeContextMenu
                    eventType={eventType}
                    onDeleteEventType={() => setDeletingEventType(eventType)}
                    deleteDisabled={eventType.event_count > 0}
                  />
                </TableData>

                {/* Delete modal */}
                <ConfirmModal
                  show={deletingEventType === eventType}
                  type={ConfirmModal.types.DANGER}
                  title={`Delete ${termFor('Event Type')}`}
                  body={`Are you sure you want to permanently delete '${eventType.name}'? This action cannot be undone.`}
                  approveLabel="Delete"
                  onApprove={() => handleDeleteEventType(eventType)}
                  onCancel={() => setDeletingEventType(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default EventTypeList;